<script>
  import { onMount, onDestroy } from 'svelte';
  import Hls from 'hls.js';

  // Element bindings
  let videoEl;

  // Reactive state
  let currentStreamName = $state(null);
  let bearerToken = $state(null);
  let tokenExpiresAt = $state(null);
  let isAuthDisabled = $state(true);
  let placardText = $state('Loading live stream\u2026');
  let hasError = $state(false);
  let isLive = $state(false);
  let showMetrics = $state(false);
  let metricsText = $state('');
  let videoAspectRatio = $state(null);

  // Non-reactive
  let hlsInstance = null;
  let tokenRenewalTimer = null;
  let metricsInterval = null;
  let liveInterval = null;
  let lastFatalType = null;
  let lastFatalTime = 0;

  // Config (loaded from config.json)
  let config = $state(null);

  const HLS_CONFIG = {
    enableWorker: true,
    lowLatencyMode: false,
    backBufferLength: 20,
    liveSyncDurationCount: 5,
    liveDurationInfinity: true,
  };

  const LIVE_EDGE_THRESHOLD = 15;
  const TOKEN_RENEW_MARGIN_MS = 10 * 60 * 1000; // 10 minutes

  // --- Lifecycle ---

  onMount(async () => {
    config = await fetchConfig();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleKeydown);
    await initializePlayer();
    liveInterval = setInterval(updateLiveState, 3000);
    metricsInterval = setInterval(updateMetrics, 1500);
  });

  onDestroy(() => {
    if (tokenRenewalTimer) clearTimeout(tokenRenewalTimer);
    if (metricsInterval) clearInterval(metricsInterval);
    if (liveInterval) clearInterval(liveInterval);
    if (hlsInstance) { hlsInstance.destroy(); hlsInstance = null; }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.removeEventListener('keydown', handleKeydown);
  });

  // --- Config ---

  async function fetchConfig() {
    const resp = await fetch('/config.json');
    return resp.json();
  }

  // --- Token Management ---

  async function fetchToken() {
    showPlacard('Authorizing\u2026');
    const resp = await fetch('/api/token');
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    bearerToken = data.token;
    isAuthDisabled = data.auth_disabled === true;
    tokenExpiresAt = data.expires_at || Date.now() + 120 * 60 * 1000;
    return data;
  }

  function scheduleTokenRenewal() {
    if (isAuthDisabled || !tokenExpiresAt) return;
    if (tokenRenewalTimer) clearTimeout(tokenRenewalTimer);

    const renewIn = Math.max(0, (tokenExpiresAt - Date.now()) - TOKEN_RENEW_MARGIN_MS);
    tokenRenewalTimer = setTimeout(renewToken, renewIn);
  }

  async function renewToken() {
    try {
      await fetchToken();
      if (hlsInstance && !isAuthDisabled && bearerToken) {
        hlsInstance.config.xhrSetup = (xhr) => {
          xhr.setRequestHeader('Authorization', `Bearer ${bearerToken}`);
        };
      }
      scheduleTokenRenewal();
    } catch {
      tokenRenewalTimer = setTimeout(renewToken, 30000);
    }
  }

  // --- Player Init ---

  async function initializePlayer() {
    try {
      await fetchToken();
      scheduleTokenRenewal();
      const saved = localStorage.getItem('preferredStream');
      const defaultStream = config?.defaultStream || '4K';
      loadStream(saved && config?.streams?.[saved] ? saved : defaultStream);
    } catch (err) {
      showPlacard(`Failed to initialize: ${err.message}`, true);
    }
  }

  // --- Stream Loading ---

  function loadStream(streamName) {
    if (!config?.streams?.[streamName]) return;
    if (currentStreamName === streamName && hlsInstance) return;

    currentStreamName = streamName;
    localStorage.setItem('preferredStream', streamName);
    showPlacard(`Loading ${streamName} stream\u2026`);

    const streamUrl = isAuthDisabled
      ? `${config.baseUrl}${config.streams[streamName]}`
      : `${config.baseUrl}${config.streams[streamName]}?jwt=${bearerToken}`;

    if (hlsInstance) { hlsInstance.destroy(); hlsInstance = null; }

    lastFatalType = null;
    lastFatalTime = 0;

    if (Hls.isSupported()) {
      const hlsConfig = {
        ...HLS_CONFIG,
        ...(isAuthDisabled ? {} : {
          xhrSetup: (xhr) => {
            if (bearerToken) xhr.setRequestHeader('Authorization', `Bearer ${bearerToken}`);
          }
        })
      };

      hlsInstance = new Hls(hlsConfig);
      hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => tryAutoplay());
      hlsInstance.on(Hls.Events.ERROR, handleHlsError);
      hlsInstance.loadSource(streamUrl);
      hlsInstance.attachMedia(videoEl);

    } else if (videoEl?.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = streamUrl;
    } else {
      showPlacard('Your browser cannot play this stream.', true);
    }
  }

  function tryAutoplay() {
    videoEl?.play().catch(() => {});
  }

  // --- Error Handling ---

  function handleHlsError(_event, data) {
    if (!data.fatal) return;

    const now = Date.now();

    // Auth error: renew token and reload
    if (!isAuthDisabled &&
        data.type === Hls.ErrorTypes.NETWORK_ERROR &&
        data.response && (data.response.code === 401 || data.response.code === 403)) {
      showPlacard('Authorization failed, retrying\u2026');
      renewToken().then(() => { if (currentStreamName) loadStream(currentStreamName); })
                  .catch(() => showPlacard('Authorization failed.', true));
      return;
    }

    // Second fatal of the same type within 30s = give up
    if (lastFatalType === data.type && now - lastFatalTime < 30000) {
      showPlacard('Stream unavailable. Please try again.', true);
      if (hlsInstance) { hlsInstance.destroy(); hlsInstance = null; }
      return;
    }

    lastFatalType = data.type;
    lastFatalTime = now;

    // One recovery attempt per error type
    switch (data.type) {
      case Hls.ErrorTypes.NETWORK_ERROR:
        if (hlsInstance) hlsInstance.startLoad();
        break;
      case Hls.ErrorTypes.MEDIA_ERROR:
        if (hlsInstance) hlsInstance.recoverMediaError();
        break;
      default:
        if (currentStreamName) loadStream(currentStreamName);
        break;
    }
  }

  // --- Placard ---

  function showPlacard(text, error = false) {
    placardText = text;
    hasError = error;
  }

  function hidePlacard() {
    placardText = null;
    hasError = false;
  }

  function retry() {
    hasError = false;
    initializePlayer();
  }

  // --- Live State ---

  function getLiveEdge() {
    if (videoEl?.seekable.length > 0) return videoEl.seekable.end(videoEl.seekable.length - 1);
    return videoEl ? videoEl.duration : NaN;
  }

  function updateLiveState() {
    if (!videoEl) return;
    const edge = getLiveEdge();
    isLive = !isNaN(edge) && isFinite(edge) && edge > 0 && (edge - videoEl.currentTime <= LIVE_EDGE_THRESHOLD);
  }

  function seekToLive() {
    if (!videoEl) return;
    const edge = getLiveEdge();
    if (!isNaN(edge) && isFinite(edge)) {
      videoEl.currentTime = edge;
      videoEl.play();
      isLive = true;
    }
  }

  // --- Metrics ---

  function updateMetrics() {
    if (!showMetrics || !videoEl) return;

    const res = videoEl.videoWidth && videoEl.videoHeight
      ? `${videoEl.videoWidth}x${videoEl.videoHeight}` : 'N/A';

    let buffer = 0;
    if (videoEl.buffered?.length > 0) {
      try { buffer = videoEl.buffered.end(videoEl.buffered.length - 1) - videoEl.currentTime; }
      catch { buffer = 0; }
    }

    let bitrate = 'N/A';
    let dropped = 0;
    if (hlsInstance) {
      const idx = hlsInstance.currentLevel;
      if (idx >= 0 && hlsInstance.levels?.[idx]?.bitrate) {
        bitrate = `${(hlsInstance.levels[idx].bitrate / 1000).toFixed(0)} kbps`;
      }
      dropped = hlsInstance.stats?.droppedFrames || 0;
    }

    const edge = getLiveEdge();
    const latency = !isNaN(edge) && isFinite(edge) && edge > 0
      ? `${(edge - videoEl.currentTime).toFixed(1)}s` : 'N/A';

    metricsText = `Res: ${res} | Bitrate: ${bitrate} | Buffer: ${buffer >= 0 ? buffer.toFixed(1) : 'N/A'}s | Latency: ${latency} | Dropped: ${dropped}`;
  }

  // --- Event Handlers ---

  function handleVisibilityChange() {
    if (document.visibilityState !== 'visible') return;
    if (!isAuthDisabled && tokenExpiresAt && (tokenExpiresAt - Date.now()) < TOKEN_RENEW_MARGIN_MS) {
      renewToken();
    }
    updateLiveState();
  }

  function handleKeydown(e) {
    if (e.key === 'd' || e.key === 'D') {
      showMetrics = !showMetrics;
    }
  }

  function handleVideoPlaying() {
    hidePlacard();
    updateLiveState();
    updateAspectRatio();
  }

  function handleVideoResize() {
    updateAspectRatio();
  }

  function updateAspectRatio() {
    if (videoEl?.videoWidth && videoEl?.videoHeight) {
      videoAspectRatio = `${videoEl.videoWidth} / ${videoEl.videoHeight}`;
    }
  }
</script>

<style>
  /* --- Video --- */
  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
    background: #000;
    object-fit: contain;
  }

  video::-webkit-media-controls-timeline {
    opacity: 0.3;
    transition: opacity 0.5s ease;
  }
  video:hover::-webkit-media-controls-timeline,
  video:focus::-webkit-media-controls-timeline {
    opacity: 1;
  }
  video::-webkit-media-controls-panel {
    background-image: linear-gradient(transparent, rgba(0, 0, 0, 0.6)) !important;
  }

  /* --- Placard --- */
  .placard {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: clamp(1rem, 2.5vw, 1.75rem);
    text-align: center;
    padding: 1rem 2rem;
    backdrop-filter: blur(3px);
    transition: opacity 0.4s ease;
    z-index: 1;
  }

  .retry-btn {
    margin-top: 1rem;
    padding: 0.5rem 1.5rem;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .retry-btn:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  /* --- Live Button --- */
  .live-btn {
    position: absolute;
    bottom: 48px;
    right: 12px;
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    background: rgba(100, 100, 100, 0.7);
    color: #fff;
    font-size: 0.9rem;
    cursor: pointer;
    user-select: none;
    transition: background 0.2s;
    z-index: 1;
  }

  .live-btn.live {
    background: rgba(255, 0, 0, 0.8);
  }

  .live-btn.live:hover {
    background: rgba(255, 0, 0, 1);
  }

  /* --- Quality Selector --- */
  .quality-selector {
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: #fff;
    padding: 6px;
    font-size: 0.9rem;
    z-index: 2;
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .quality-btn {
    background: rgba(80, 80, 80, 0.5);
    border: none;
    color: #fff;
    padding: 4px 8px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: background 0.2s;
  }

  .quality-btn:hover {
    background: rgba(100, 100, 100, 0.8);
  }

  .quality-btn.active {
    background: rgba(0, 123, 255, 0.7);
    font-weight: bold;
  }

  /* --- Social Link --- */
  .social {
    text-align: center;
  }

  .social a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #fff;
    text-decoration: none;
    background: rgba(0, 0, 0, 0.5);
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 0.9rem;
    transition: background 0.2s;
  }

  .social a:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  /* --- Metrics --- */
  .metrics {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    color: white;
    font-size: 9px;
    padding: 3px 6px;
    font-family: monospace;
    z-index: 10;
    text-align: left;
    line-height: 1.3;
    backdrop-filter: blur(2px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* --- Footer --- */
  .footer {
    position: fixed;
    bottom: 8px;
    right: 12px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.75rem;
    z-index: 1;
  }

  .footer a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    transition: color 0.2s;
  }

  .footer a:hover {
    color: #fff;
  }

  /* --- Container --- */
  .main-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  /* --- Title --- */
  .title {
    color: #fff;
    font-size: clamp(1.1rem, 2.5vw, 1.5rem);
    font-weight: 600;
    text-align: center;
    margin-bottom: 10px;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
    letter-spacing: 0.02em;
  }

  /* --- Video Wrapper --- */
  .video-wrapper {
    position: relative;
    width: 90vw;
    max-width: 1600px;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
    margin-bottom: 12px;
    background-color: #000;
    max-height: 80vh;
    z-index: 0;
  }

  /* --- Mobile --- */
  @media (max-width: 768px) {
    .title {
      margin-bottom: 0;
      padding: 12px 0 8px;
    }

    .video-wrapper {
      width: 100%;
      border-radius: 0;
      box-shadow: none;
      margin-bottom: 0;
      max-height: none;
    }

    .live-btn {
      bottom: 56px;
      right: 16px;
    }

    .quality-selector {
      top: 8px;
      right: 8px;
      font-size: 0.8rem;
    }

    .quality-btn {
      padding: 3px 6px;
      font-size: 0.7rem;
    }

    .placard {
      font-size: 1rem;
    }

    .metrics {
      font-size: 8px;
      padding: 2px 5px;
    }

    .social {
      margin-top: 16px;
    }

    .footer {
      position: static;
      text-align: center;
      padding: 12px 0 16px;
    }
  }
</style>

<div class="main-container">
  <h1 class="title">Alberta Goose Cam</h1>
  <div class="video-wrapper" style:aspect-ratio={videoAspectRatio}>
    {#if placardText}
      <div class="placard">
        {placardText}
        {#if hasError}
          <button class="retry-btn" onclick={retry}>Retry</button>
        {/if}
      </div>
    {/if}

    <video
      bind:this={videoEl}
      controls
      muted
      autoplay
      playsinline
      preload="auto"
      onplaying={handleVideoPlaying}
      onpause={updateLiveState}
      onseeked={updateLiveState}
      onresize={handleVideoResize}
    ></video>

    <button
      type="button"
      class="live-btn"
      class:live={isLive}
      onclick={seekToLive}
      title={isLive ? 'Playing Live' : 'Click to jump to Live'}
    >LIVE</button>

    {#if config?.streams && currentStreamName}
      <div class="quality-selector">
        <span>Quality:</span>
        {#each Object.keys(config.streams) as name}
          <button
            type="button"
            class="quality-btn"
            class:active={currentStreamName === name}
            onclick={() => loadStream(name)}
          >{name}</button>
        {/each}
      </div>
    {/if}
  </div>

  <div class="social">
    {#if config?.youtubeUrl}
      <a href={config.youtubeUrl} target="_blank" rel="noopener">
        <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18" fill="#ff0000">
          <path d="M23.498 6.186a2.986 2.986 0 0 0-2.103-2.103C19.44 3.5 12 3.5 12 3.5s-7.44 0-9.395.583A2.986 2.986 0 0 0 .502 6.186 31.137 31.137 0 0 0 0 12a31.137 31.137 0 0 0 .502 5.814 2.986 2.986 0 0 0 2.103 2.103C4.56 20.5 12 20.5 12 20.5s7.44 0 9.395-.583a2.986 2.986 0 0 0 2.103-2.103A31.137 31.137 0 0 0 24 12a31.137 31.137 0 0 0-.502-5.814ZM9.75 15.5v-7L15.5 12l-5.75 3.5Z" />
        </svg>
        Watch on YouTube
      </a>
    {/if}
  </div>

  <div class="footer">a <a href="https://slepp.ca" target="_blank" rel="noopener">slepp</a> project</div>
</div>

{#if showMetrics}
  <div class="metrics">{metricsText}</div>
{/if}
